import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../config/api";

// Użytkownicy, którzy dostają rozszerzony eksport XLSX (pola time-measure + historia zmian).
// Wspólne dla UserDashboard, Archiwum odpraw, Odpraw Tranzyt i Archiwum Tranzyt.
export const TIME_MEASURE_EXPORT_USERS = [
  "Maciej",
  "plomza",
  "dczernikowska",
  "pnowak",
  "adobrowolska",
];

export const isTimeMeasureExportUser = (username) =>
  TIME_MEASURE_EXPORT_USERS.includes(username);

// Konfiguracja grup historii zmian: pole w odpowiedzi backendu, prefiks kolumn i klucze
// w obiekcie opcji (mapy po id rekordu + maksymalna liczba zmian = liczba grup kolumn).
const HISTORY_GROUPS = [
  {
    responseKey: "osobaPrzygotowujacaHistory",
    label: "Os. przygotowująca",
    byId: "osobaHistoryById",
    max: "maxOsobaChanges",
  },
  {
    responseKey: "lokalizacjaTowaruHistory",
    label: "Lokalizacja towaru",
    byId: "lokalizacjaHistoryById",
    max: "maxLokalizacjaChanges",
  },
  {
    responseKey: "statusRealizacjiHistory",
    label: "Status",
    byId: "statusHistoryById",
    max: "maxStatusChanges",
  },
  {
    responseKey: "rodzajTransportuHistory",
    label: "Rodzaj transportu",
    byId: "rodzajTransportuHistoryById",
    max: "maxRodzajTransportuChanges",
  },
];

// Grupuje wiersze historii (customId, nrRef, oldValue, newValue, editedBy, editedAt)
// po customId, zachowując kolejność chronologiczną zwróconą przez backend.
const groupHistoryByCustomId = (rows) => {
  const map = {};
  (rows || []).forEach((row) => {
    if (!map[row.customId]) {
      map[row.customId] = [];
    }
    map[row.customId].push(row);
  });
  return map;
};

// Pobiera z backendu dane do rozszerzonego eksportu dla podanych wierszy (id z tabeli
// `dane`) i zwraca obiekt opcji dla appendTimeMeasureColumns / getTimeMeasureDateKeys.
// Przy błędzie zwraca puste opcje (eksport zawiera wtedy tylko standardowe pola).
export async function fetchTimeMeasureExportOptions(data, headers) {
  const options = {};
  const ids = data.map((row) => row.id).filter((id) => id != null);

  if (ids.length === 0) {
    console.warn(
      "[time-measure export] brak id w eksportowanych wierszach - nie pobieram danych time-measure.",
    );
    return options;
  }

  try {
    const response = await axios.get(
      `${API_BASE_URL}/time-measure-export-data`,
      {
        params: { ids: ids.join(",") },
        headers,
      },
    );

    options.timeMeasureFieldsById = (response.data?.fields || []).reduce(
      (acc, field) => {
        acc[field.id] = field;
        return acc;
      },
      {},
    );

    HISTORY_GROUPS.forEach(({ responseKey, byId, max }) => {
      options[byId] = groupHistoryByCustomId(response.data?.[responseKey]);
      options[max] = Object.values(options[byId]).reduce(
        (acc, arr) => Math.max(acc, arr.length),
        0,
      );
    });
  } catch (error) {
    console.error("Error fetching time-measure export data:", error);
    toast.error(
      `Nie udało się pobrać dodatkowych danych time-measure (${
        error.response?.status || error.message
      }) - eksport zawiera tylko standardowe pola.`,
    );
    return {};
  }

  return options;
}

// Dopisuje do wiersza rekordu kolumny time-measure oraz historię zmian (jako kolejne
// ponumerowane grupy kolumn w TYM SAMYM wierszu, a nie w osobnych arkuszach - dzięki temu
// każdą zmianę danego zgłoszenia widać od razu obok niego). Nazwy kolumn są już docelowe.
// Bez opcji (zwykły eksport) nic nie robi.
export function appendTimeMeasureColumns(newRow, options = {}) {
  if (newRow.id == null) {
    return;
  }

  const extra = options.timeMeasureFieldsById?.[newRow.id];
  if (extra) {
    newRow["Data zmiany statusu na Wysłane"] = extra.dataStatusWyslane;
    newRow["Data zmiany rodzaju transportu na Na Kołach"] =
      extra.dataZmianyNaKolach;
    newRow["Znacznik oczekiwanie na rozładunek / odprawa łączona"] =
      extra.oczekiwanieNaRozladunekLubOdprawaLaczonaBox ? "Tak" : "Nie";
  }

  HISTORY_GROUPS.forEach(({ label, byId, max }) => {
    if (!options[byId]) {
      return;
    }
    const changes = options[byId][newRow.id] || [];
    for (let i = 0; i < (options[max] || 0); i++) {
      const change = changes[i];
      const n = i + 1;
      newRow[`${label} zm.${n} - z`] = change?.oldValue;
      newRow[`${label} zm.${n} - na`] = change?.newValue;
      newRow[`${label} zm.${n} - kto`] = change?.editedBy;
      newRow[`${label} zm.${n} - kiedy`] = change?.editedAt;
    }
  });
}

// Nagłówki kolumn z datą/godziną dodane przez rozszerzony eksport (do formatowania komórek).
export function getTimeMeasureDateKeys(options = {}) {
  const keys = [];
  if (options.timeMeasureFieldsById) {
    keys.push(
      "Data zmiany statusu na Wysłane",
      "Data zmiany rodzaju transportu na Na Kołach",
    );
  }
  HISTORY_GROUPS.forEach(({ label, max }) => {
    for (let i = 1; i <= (options[max] || 0); i++) {
      keys.push(`${label} zm.${i} - kiedy`);
    }
  });
  return keys;
}
